
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PEOPLE } from '../data/GET_PEOPLE';

/** `?tags=3,7` — kept in the URL alongside `search` so a filtered list is linkable. */
const TAGS_PARAM = 'tags';

const parseTagIds = (params: URLSearchParams): number[] =>
    (params.get(TAGS_PARAM) ?? '')
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

function usePeopleSearch() {
    const navigate = useNavigate();
    const location = useLocation();
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get('search') || ''
    );
    const [tagIds, setTagIds] = useState<number[]>(
        () => parseTagIds(new URLSearchParams(location.search))
    );
    const [hasSearched, setHasSearched] = useState(false);
    const { loading, error, data, refetch } = useQuery(GET_PEOPLE, { variables: { search, tagIds } });
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Both filters live in one query string, so either one changing has to carry the other.
    const buildSearchString = useCallback((nextSearch: string, nextTagIds: number[]) => {
        const params = new URLSearchParams();
        params.set('search', nextSearch);
        if (nextTagIds.length > 0) params.set(TAGS_PARAM, nextTagIds.join(','));
        return `?${params.toString()}`;
    }, []);

    const handleSearchChange = useCallback((_: any, change: any) => {
        if (!hasSearched) setHasSearched(true);
        clearTimeout(timeoutRef.current);
        setSearch(change.value);
        timeoutRef.current = setTimeout(() => {
            navigate({ pathname: location.pathname, search: buildSearchString(change.value, tagIds) });
            refetch({ search: change.value, tagIds });
        }, 500);
    }, [refetch, hasSearched, navigate, location.pathname, tagIds, buildSearchString]);

    // Picking from a dropdown is a deliberate act, not typing — so no debounce here; the
    // 500ms wait that stops a keystroke-per-request would just feel like lag.
    const handleTagsChange = useCallback((nextTagIds: number[]) => {
        clearTimeout(timeoutRef.current);
        setTagIds(nextTagIds);
        navigate({ pathname: location.pathname, search: buildSearchString(search, nextTagIds) });
        refetch({ search, tagIds: nextTagIds });
    }, [refetch, navigate, location.pathname, search, buildSearchString]);

    // Sync state with URL on browser back/forward
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const param = params.get('search');
        const urlTagIds = parseTagIds(params);
        const tagsDiffer = urlTagIds.join(',') !== tagIds.join(',');

        if (param === null) {
            setSearch('');
            setHasSearched(false);
            if (tagsDiffer) setTagIds(urlTagIds);
            refetch({ search: '', tagIds: urlTagIds });
        } else if (param !== search || tagsDiffer) {
            setSearch(param);
            setHasSearched(true);
            if (tagsDiffer) setTagIds(urlTagIds);
            refetch({ search: param, tagIds: urlTagIds });
        }
    }, [location.search, refetch]);

    const hasTagFilter = tagIds.length > 0;

    // A tag filter can legitimately return few or no people, and the search box has to stay
    // on screen while it does — otherwise narrowing to an empty result removes the control
    // you need to widen it again.
    const shouldShowSearch =
        (data?.users.length > 10) || search.length > 0 || hasSearched || hasTagFilter;

    return {
        search,
        tagIds,
        hasTagFilter,
        loading,
        error,
        data,
        handleSearchChange,
        handleTagsChange,
        shouldShowSearch,
    };
}

export { usePeopleSearch };
